import { Link } from 'react-router-dom';
import { Shield, Users, Handshake, Rocket, Building, TrendingUp, ArrowRight, Award, DollarSign, CheckCircle } from 'lucide-react';

const HERO_IMAGE = "https://images.pexels.com/photos/8941650/pexels-photo-8941650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export const Home = () => {
  return (
    <div className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Kids playing sports" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/30"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Players Health Foundation
            </p>
            <h1 className="text-5xl sm:text-6xl tracking-tight font-extrabold text-[#272926] leading-tight mb-6">
              Protecting kids.<br />Powering possibilities.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed text-zinc-600 mb-8 max-w-xl">
              The Players Health Foundation is a catalyst for safer, more accessible youth sports — so every child has the opportunity to play, grow, and thrive.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/about"
                className="btn-primary inline-flex items-center justify-center"
                data-testid="hero-learn-more-btn"
              >
                Learn More
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug mb-8">
              Too many kids are being left out or put at risk.
            </h2>
            <p className="text-lg leading-relaxed text-zinc-600 mb-6">
              Youth sports should be a place where kids build confidence, community, and lifelong skills.
            </p>
            <p className="text-lg leading-relaxed text-zinc-600 mb-6">
              But today, rising costs are pushing families out, and too many programs lack the resources and training needed to keep athletes safe.
            </p>
            <p className="text-xl font-semibold text-[#5B4FF9]">
              We believe every child deserves a safe place to play and the opportunity to stay in the game.
            </p>
          </div>
        </div>
      </section>

      {/* Your Approach Section */}
      <section className="py-20 md:py-32" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Our Approach
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              Where safety meets access
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Safety */}
            <div className="text-center p-8" data-testid="approach-safety">
              <div className="w-16 h-16 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="h-8 w-8 text-[#5B4FF9]" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Safety</h3>
              <p className="text-zinc-600 leading-relaxed">
                We help organizations create safer environments through training, policies, and resources that protect young athletes.
              </p>
            </div>

            {/* Access */}
            <div className="text-center p-8" data-testid="approach-access">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Access</h3>
              <p className="text-zinc-600 leading-relaxed">
                We reduce financial barriers by supporting costs like registration fees, equipment, and travel.
              </p>
            </div>

            {/* Connection */}
            <div className="text-center p-8" data-testid="approach-connection">
              <div className="w-16 h-16 bg-[#F5F4FF] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Handshake className="h-8 w-8 text-[#5B4FF9]" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Connection</h3>
              <p className="text-zinc-600 leading-relaxed">
                We bring together partners, communities, and the broader sports ecosystem to drive meaningful change at scale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Grant Program Section - NEW */}
      <section className="py-20 md:py-32 bg-[#5B4FF9]" data-testid="grants-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-[0.2em] font-bold text-white/80 mb-4">
                PHF Grant Program
              </p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white leading-snug mb-6">
                Safer sports. More kids playing.
              </h2>
              <p className="text-lg leading-relaxed text-white/90 mb-8">
                The PHF Grant Program provides funding and safety resources to youth sports organizations committed to removing financial barriers to participation — and protecting every athlete in their care.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/grants"
                  className="bg-white text-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                  data-testid="home-grants-btn"
                >
                  Learn About Grants
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <a
                  href="https://playershealthfoundation.submittable.com/submit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent text-white border-2 border-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:bg-white/10 inline-flex items-center justify-center"
                  data-testid="home-apply-btn"
                >
                  Start Application
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <DollarSign className="h-8 w-8 text-white mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Accessibility Funding</h3>
                <p className="text-white/80 text-sm">Registration, equipment, travel & more</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <Shield className="h-8 w-8 text-white mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Safety Resources</h3>
                <p className="text-white/80 text-sm">Training, policies & compliance tools</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <Award className="h-8 w-8 text-white mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Safety Hub Access</h3>
                <p className="text-white/80 text-sm">Free for all grant recipients</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <CheckCircle className="h-8 w-8 text-white mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Abuse Prevention</h3>
                <p className="text-white/80 text-sm">Training for all adult staff</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-32 bg-zinc-50" data-testid="what-we-do-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              What We Do
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug">
              How we create impact
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="pillar-fuel">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-xl flex items-center justify-center mb-6">
                <Rocket className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Fuel Access & Safety</h3>
              <p className="text-zinc-600 leading-relaxed">
                We provide funding and resources to organizations working to make sports safer and more accessible for youth athletes.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="pillar-activate">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-xl flex items-center justify-center mb-6">
                <Building className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Activate the Sports Ecosystem</h3>
              <p className="text-zinc-600 leading-relaxed">
                Through partnerships with leagues, organizations, and industry leaders, we expand our reach and amplify impact.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-8 shadow-sm card-hover" data-testid="pillar-build">
              <div className="w-12 h-12 bg-[#5B4FF9] rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#272926] mb-4">Build for Long-Term Impact</h3>
              <p className="text-zinc-600 leading-relaxed">
                We are building a sustainable model grounded in transparency, measurement, and a commitment to lasting change.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Early Stage Section */}
      <section className="py-20 md:py-32 bg-[#F5F4FF]" data-testid="early-stage-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#5B4FF9] mb-4">
              Our Journey
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#272926] leading-snug mb-8">
              We're just getting started
            </h2>
            <p className="text-lg leading-relaxed text-zinc-600 mb-6">
              The Players Health Foundation is in the early stages of building programs, partnerships, and funding initiatives designed to create lasting impact.
            </p>
            <p className="text-lg leading-relaxed text-zinc-600">
              We are focused on launching our first grant programs, developing resources for communities, and growing a network of partners committed to safer, more accessible youth sports.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#272926]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-white leading-snug mb-8">
              Join us in building a better future for youth athletes
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://givebutter.com/players-health-foundation-website-button-suv1ls"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#5B4FF9] text-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                data-testid="cta-donate-btn"
              >
                Donate Now
              </a>
              <Link
                to="/grants"
                className="bg-transparent text-white border-2 border-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:bg-white/10 inline-flex items-center justify-center"
                data-testid="cta-grants-btn"
              >
                Apply for Grants
              </Link>
              <Link
                to="/get-involved"
                className="bg-transparent text-white border-2 border-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 hover:bg-white/10 inline-flex items-center justify-center"
                data-testid="cta-partner-btn"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
